<template>
  <div class="custom-carousel" >
    <!-- Main Image Display -->
    <div class="carousel-main-container" 
         @click="openFullscreen" 
         ref="carouselContainer"
         v-touch="{
           left: nextSlide,
           right: prevSlide,
           up: () => {},
           down: () => {}
         }"
         :class="`aspect-ratio-${currentAspectRatio}`">
      <v-responsive :aspect-ratio="aspectRatioValue">
        <transition :name="transitionName">
          <div :key="page" class="carousel-slide">
            <!-- Current Attachment -->
            <template v-if="currentMedia.type === 'attachment'">
              <AttachmentFrame :value="currentMedia.content" :viewOnly="viewOnly" />
              
              <v-btn v-if="!viewOnly" class="elevation-0 delete-btn" color="error" dark fab x-small
                @click.stop="deleteMedia('attachment', currentMedia.index)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>

            <!-- Current YouTube -->
            <template v-else-if="currentMedia.type === 'youtube'">
              <YoutubeFrame :value="currentMedia.content" />
              
              <v-btn v-if="!viewOnly" class="elevation-0 delete-btn" color="error" dark fab x-small
                @click.stop="deleteMedia('youtube', currentMedia.index)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>

            <!-- Current Spotify -->
            <template v-else-if="currentMedia.type === 'spotify'">
              <SpotifyFrame :value="currentMedia.content" />
              
              <v-btn v-if="!viewOnly" class="elevation-0 delete-btn" color="error" dark fab x-small
                @click.stop="deleteMedia('spotify', currentMedia.index)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
          </div>
        </transition>
      </v-responsive>

      <!-- Navigation Arrows -->
      <div v-if="mediaLength > 1" class="carousel-arrows">
        <v-btn icon class="carousel-arrow carousel-arrow-left" @click.stop="prevSlide">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon class="carousel-arrow carousel-arrow-right" @click.stop="nextSlide">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Indicators -->
    <div v-if="mediaLength > 1" class="carousel-indicators mb-2">
      <span 
        v-for="i in mediaLength" 
        :key="`indicator-${i}`" 
        class="carousel-indicator" 
        :class="{ 'active': page === i - 1 }"
        @click="page = i - 1">
      </span>
    </div>

    <!-- Fullscreen Modal -->
    <v-dialog fullscreen v-model="fullscreen" 
      v-touch="{
        left: nextSlide,
        right: prevSlide,
        up: () => {},
        down: () => {}
      }">
      <v-card class="fullscreen-container rounded-0 px-0" dark>
        <transition :name="transitionName">
          <div :key="page" class="fullscreen-slide">
            <!-- Current Attachment -->
            <template v-if="currentMedia.type === 'attachment'">
              <img v-if="currentMedia.content.type && currentMedia.content.type.startsWith('image/')" 
                :src="currentMedia.content.url" 
                :alt="currentMedia.content.name"
                class="fullscreen-image" 
                @touchstart="handleTouchStart"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd" />
                
              <video v-if="currentMedia.content.type && !currentMedia.content.type.startsWith('image/')" 
                :src="currentMedia.content.url" 
                controls
                class="fullscreen-video"></video>
            </template>

            <!-- Current YouTube -->
            <template v-else-if="currentMedia.type === 'youtube'">
              <YoutubeFrame :value="currentMedia.content" />
            </template>

            <!-- Current Spotify -->
            <template v-else-if="currentMedia.type === 'spotify'">
              <SpotifyFrame :value="currentMedia.content" />
            </template>
          </div>
        </transition>

        <!-- Fullscreen Navigation Arrows -->
        <div v-if="mediaLength > 1" class="carousel-arrows">
          <v-btn icon large class="carousel-arrow carousel-arrow-left" @click.stop="prevSlide">
            <v-icon large>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn icon large class="carousel-arrow carousel-arrow-right" @click.stop="nextSlide">
            <v-icon large>mdi-chevron-right</v-icon>
          </v-btn>
        </div>

        <!-- Fullscreen Indicators -->
        <div v-if="mediaLength > 1" class="carousel-indicators-container">
          <div class="carousel-indicators fullscreen-indicators">
            <span 
              v-for="i in mediaLength" 
              :key="`fullscreen-indicator-${i}`" 
              class="carousel-indicator" 
              :class="{ 'active': page === i - 1 }"
              @click="page = i - 1">
            </span>
          </div>
        </div>

        <!-- Close Button -->
        <v-btn icon dark class="fullscreen-close" @click.stop="fullscreen = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AttachmentFrame from "./AttachmentFrame.vue";
import SpotifyFrame from "./SpotifyFrame.vue";
import YoutubeFrame from "./YoutubeFrame.vue";

export default {
  name: "MediaCarousel",

  components: {
    AttachmentFrame,
    SpotifyFrame,
    YoutubeFrame,
  },

  props: {
    attachments: {
      type: Array,
      default: () => []
    },
    youtubeLinks: {
      type: Array,
      default: () => []
    },
    spotifyLinks: {
      type: Array,
      default: () => []
    },
    viewOnly: Boolean,
    defaultAspectRatio: {
      type: String,
      default: "4:5",
      validator: (val) => ['4:5', '16:9', '1:1'].includes(val)
    }
  },

  data() {
    return {
      page: 0,
      prevPage: 0,
      preloadedImages: [],
      fullscreen: false,
      touchStartX: 0,
      touchEndX: 0,
      touchStartY: 0,
      touchEndY: 0,
      slideDirection: 'next',
      swipeThreshold: 50, // minimum distance to register a swipe
      mediaAspectRatios: {}, // Store detected aspect ratios for media items
      currentAspectRatio: this.defaultAspectRatio // Initialize with default
    };
  },

  mounted() {
    this.preloadAttachmentImages();
    this.addTouchEventListeners();
    this.addKeyboardListeners();
  },

  beforeDestroy() {
    this.removeTouchEventListeners();
    this.removeKeyboardListeners();
  },

  methods: {
    nextSlide() {
      this.prevPage = this.page;
      this.slideDirection = 'next';
      
      if (this.page < this.mediaLength - 1) {
        this.page++;
      } else {
        this.page = 0;
      }
    },
    
    prevSlide() {
      this.prevPage = this.page;
      this.slideDirection = 'prev';
      
      if (this.page > 0) {
        this.page--;
      } else {
        this.page = this.mediaLength - 1;
      }
    },
    
    deleteMedia(type, index) {
      // Emit the appropriate event based on media type
      if (type === 'attachment') {
        this.$emit('deleteAttachment', index);
      } else if (type === 'youtube') {
        this.$emit('deleteYoutubeLink', index);
      } else if (type === 'spotify') {
        this.$emit('deleteSpotifyLink', index);
      }
      
      // Adjust page if necessary
      if (this.page >= this.mediaLength - 1) {
        // If we were on the last page, go to the new last page
        this.page = Math.max(0, this.mediaLength - 2);
      }
    },
    
    openFullscreen(event) {
      // Don't open fullscreen for videos or spotify/youtube frames
      const target = event.target;
      if (target.tagName === 'VIDEO' || 
          target.closest('.v-btn') || 
          target.closest('iframe')) {
        return;
      }
      
      this.fullscreen = true;
    },

    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    },
    
    handleTouchMove(event) {
      this.touchEndX = event.touches[0].clientX;
      this.touchEndY = event.touches[0].clientY;
      
      // Calculate horizontal and vertical differences
      const diffX = this.touchStartX - this.touchEndX;
      const diffY = this.touchStartY - this.touchEndY;
      
      // Only prevent default if horizontal swipe is stronger than vertical
      if (Math.abs(diffX) > Math.abs(diffY)) {
        event.preventDefault();
      }
    },
    
    handleTouchEnd(event) {
      // Calculate horizontal difference
      const diffX = this.touchStartX - this.touchEndX;
      
      // If absolute difference is greater than the threshold, register a swipe
      if (Math.abs(diffX) > this.swipeThreshold) {
        if (diffX > 0) {
          // Swiped left - go to next slide
          this.nextSlide();
        } else {
          // Swiped right - go to previous slide
          this.prevSlide();
        }
      }
      
      // Reset touch positions
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.touchStartY = 0;
      this.touchEndY = 0;
    },

    handleKeyDown(event) {
      if (!this.fullscreen) return;
      
      if (event.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (event.key === 'ArrowRight') {
        this.nextSlide();
      } else if (event.key === 'Escape') {
        this.fullscreen = false;
      }
    },

    addKeyboardListeners() {
      window.addEventListener('keydown', this.handleKeyDown);
    },

    removeKeyboardListeners() {
      window.removeEventListener('keydown', this.handleKeyDown);
    },

    addTouchEventListeners() {
      // Most touch events are handled directly on elements or by Vuetify's v-touch directive
    },

    removeTouchEventListeners() {
      // No manual cleanup needed for direct element handlers
    },

    preloadAttachmentImages() {
      // Clean up any existing preloaded images
      this.preloadedImages.forEach(img => img.remove());
      this.preloadedImages = [];
      
      // Create and load new images
      if (this.attachments && this.attachments.length) {
        this.attachments.forEach((attachment, index) => {
          const imgSrc = this.getImageSrc(attachment);
          if (imgSrc) {
            const img = new Image();
            img.onload = () => {
              // Detect and store aspect ratio for this image
              const ratio = img.naturalWidth / img.naturalHeight;
              let aspectRatioType = '4:5'; // default
              
              if (ratio > 1.7) {
                aspectRatioType = '16:9';
              } else if (ratio >= 0.9 && ratio <= 1.1) {
                aspectRatioType = '1:1';
              }
              
              this.$set(this.mediaAspectRatios, index, aspectRatioType);
              
              // Update current aspect ratio if this is the current image
              if (index === this.page) {
                this.currentAspectRatio = aspectRatioType;
              }
            };
            img.src = imgSrc;
            this.preloadedImages.push(img);
          }
        });
      }
    },

    getImageSrc(attachment) {
      // Return the image URL if it's a string, or extract it from an object if needed
      if (typeof attachment === 'string') {
        return attachment;
      }
      
      // Handle video types (use thumbnail) or image types (use url directly)
      if (attachment.type && attachment.type.startsWith('video/')) {
        return attachment.thumbnail || '';
      }
      
      return attachment.url || attachment.src || attachment.path || '';
    }
  },

  computed: {
    mediaLength() {
      return this.attachments.length + this.youtubeLinks.length + this.spotifyLinks.length;
    },
    
    transitionName() {
      return this.slideDirection === 'next' ? 'slide-left' : 'slide-right';
    },

    aspectRatioValue() {
      // Convert aspect ratio string to numeric value for v-responsive
      switch(this.currentAspectRatio) {
        case '16:9':
          return 16/9;
        case '1:1':
          return 1;
        case '4:5':
        default:
          return 4/5;
      }
    },

    currentMedia() {
      // Calculate what type of media is currently showing based on the page number
      const attachmentsLength = this.attachments.length;
      const youtubeLinksLength = this.youtubeLinks.length;

      if (this.page < attachmentsLength) {
        // For attachments, try to use the detected aspect ratio
        if (this.mediaAspectRatios[this.page]) {
          this.currentAspectRatio = this.mediaAspectRatios[this.page];
        } else {
          this.currentAspectRatio = this.defaultAspectRatio;
        }
        
        return {
          type: 'attachment',
          content: this.attachments[this.page],
          index: this.page
        };
      } else if (this.page < attachmentsLength + youtubeLinksLength) {
        // YouTube is typically 16:9
        this.currentAspectRatio = '16:9';
        
        const youtubeIndex = this.page - attachmentsLength;
        return {
          type: 'youtube',
          content: this.youtubeLinks[youtubeIndex],
          index: youtubeIndex
        };
      } else {
        // Spotify is typically square (1:1)
        this.currentAspectRatio = '1:1';
        
        const spotifyIndex = this.page - attachmentsLength - youtubeLinksLength;
        return {
          type: 'spotify',
          content: this.spotifyLinks[spotifyIndex],
          index: spotifyIndex
        };
      }
    }
  },

  watch: {
    attachments: {
      handler() {
        this.preloadAttachmentImages();
      },
      deep: true
    },
    mediaLength: {
      handler(newLength) {
        // If page is out of bounds after deletion, reset to a valid page
        if (this.page >= newLength && newLength > 0) {
          this.page = newLength - 1;
        } else if (newLength === 0) {
          this.page = 0;
        }
      }
    }
  }
};
</script>

<style>
.custom-carousel {
  width: 100%;
  margin: 0 auto;
  position: relative;
}

/* Simple, reliable carousel container */
.carousel-main-container {
  position: relative;
  width: 100%;
  background-color: #121212;
  border-radius: 4px;
  overflow: hidden; /* Prevent slide content from spilling outside */
  transition: all 0.3s ease;
}

/* Dynamic aspect ratio classes */
.aspect-ratio-4\:5 {
  aspect-ratio: 4/5;
}

.aspect-ratio-16\:9 {
  aspect-ratio: 16/9;
}

.aspect-ratio-1\:1 {
  aspect-ratio: 1/1;
}

/* Pure CSS for perfect image containers */
.carousel-slide {
  display: block;
  width: 100%; 
  height: 100%;
  text-align: center;
  position: absolute; /* Required for proper transition positioning */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

/* Perfect image display */
.carousel-slide img,
.carousel-slide video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Vuetify specific fixes */
.v-image {
  display: inline-block !important;
  width: 100%;
  height: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

.v-responsive__content {
  height: 100% !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-carousel .v-image__image {
  background-size: contain !important;
}

/* Navigation position fixes */
.carousel-arrows {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
}

.carousel-arrow {
  pointer-events: auto;
  background-color: rgba(0, 0, 0, 0.5) !important;
  margin: 0 8px;
}

/* Fix delete button positioning */
.delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
}

/* Fullscreen mode */
.fullscreen-container {
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative; /* Needed for transitions */
}

.fullscreen-slide {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute; /* Required for transitions */
}

.fullscreen-image,
.fullscreen-video {
  max-width: 95vw;
  max-height: 95vh;
  width: auto !important;
  height: auto !important;
  object-fit: contain;
}

.carousel-indicators {
  position: relative;
  display: flex;
  justify-content: center;
  margin-top: -20px;
  z-index: 10;
}

.carousel-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin: 0 4px;
  background-color: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s;
}

.carousel-indicator.active {
  background-color: var(--v-secondary-base);
}

/* Slide transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.5s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  will-change: transform, opacity;
}

.slide-left-enter {
  transform: translateX(100%);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-right-enter {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.carousel-main-container .v-responsive {
  width: 100%;
  height: 100%;
}

/* Ensure transitions don't affect layout flow */
.v-responsive__content > .transition-group {
  position: absolute;
  width: 100%;
  height: 100%;
}

/* Fullscreen styles */
.fullscreen-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.5) !important;
  z-index: 10;
}

.carousel-indicators-container {
  position: fixed;
  bottom: 40px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 10;
}

.fullscreen-indicators {
  background-color: rgba(0, 0, 0, 0.5);
  padding: 10px 15px;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  width: fit-content;
}

.fullscreen-indicators .carousel-indicator {
  width: 12px;
  height: 12px;
  margin: 0 6px;
  background-color: rgba(255, 255, 255, 0.3);
}

.fullscreen-indicators .carousel-indicator.active {
  background-color: #fff;
}

/* Note: To truly implement Instagram's solution, you should also:
 * 1. Process images on upload (resize, compress, create multiple sizes)
 * 2. Serve different sized images based on screen size
 * 3. Use progressive loading techniques
 */
</style>